import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import './ToneArchetypeRequiredModal.css';

/**
 * Blocking modal shown to already-onboarded users who have not yet selected
 * a tone archetype.  It cannot be dismissed without completing the selection.
 *
 * Usage:
 *   <ToneArchetypeRequiredModal onComplete={() => setShowModal(false)} />
 *
 * onComplete is called after a successful PUT /api/v1/users/me/tone-archetype.
 */
function ToneArchetypeRequiredModal({ onComplete }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';

  const [archetypes, setArchetypes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Mount-only fetch. Do NOT depend on ``t`` — its reference flips
    // whenever i18next finishes loading locale files asynchronously,
    // and a refetch on every flip races with the first request and can
    // leave the modal stuck on ``loading=true``.
    let cancelled = false;
    const fetchArchetypes = async () => {
      try {
        const response = await apiClient.get('/api/v1/tone-archetypes');
        if (!cancelled) setArchetypes(response.data);
      } catch (err) {
        console.error('Failed to fetch archetypes:', err);
        if (!cancelled) setError(t('onboarding.tone.error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchArchetypes();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async () => {
    if (!selectedId) return;

    try {
      setSubmitting(true);
      setError(null);
      await apiClient.put('/api/v1/users/me/tone-archetype', { archetype_id: selectedId });
      onComplete(selectedId);
    } catch (err) {
      console.error('Failed to save tone archetype:', err);
      setError(err.response?.data?.detail || t('onboarding.tone.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    /* Overlay — no onClick handler so user cannot dismiss by clicking outside */
    <div className="tone-required-overlay" role="dialog" aria-modal="true">
      <div className="tone-required-modal">
        <div className="tone-required-header">
          <h2>{t('onboarding.tone.title')}</h2>
          <p className="tone-required-subtitle">{t('onboarding.tone.subtitle')}</p>
        </div>

        {error && <div className="tone-required-error">{error}</div>}

        {loading ? (
          <div className="tone-required-loading">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="archetype-grid">
            {archetypes.map((archetype) => (
              <button
                key={archetype.id}
                type="button"
                className={`archetype-card ${selectedId === archetype.id ? 'selected' : ''}`}
                onClick={() => setSelectedId(archetype.id)}
              >
                <span className="archetype-icon">{archetype.icon}</span>
                <span className="archetype-label">{archetype[`label_${lang}`]}</span>
                <span className="archetype-tagline">{archetype[`tagline_${lang}`]}</span>
                <span className="archetype-description">{archetype[`short_description_${lang}`]}</span>
              </button>
            ))}
          </div>
        )}

        <p className="tone-required-notice">{t('onboarding.tone.notice')}</p>

        <button
          className="btn btn-primary btn-large tone-required-confirm"
          onClick={handleConfirm}
          disabled={!selectedId || submitting || loading}
        >
          {submitting ? t('onboarding.tone.confirming') : t('onboarding.tone.confirmButton')}
        </button>
      </div>
    </div>
  );
}

export default ToneArchetypeRequiredModal;
